import React, { useEffect, useState } from 'react';
import axios from '@/utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import * as XLSX from 'xlsx';
import { FaFileExport } from 'react-icons/fa';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import dayjs from 'dayjs';

interface StockMovement {
  id: string;
  createdAt: string;
  qty: number;
  productName: string;
  locationName: string;
  movementType?: string; // kalau ada di backend, tambahkan
}


const StockMovementsList = () => {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const { logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}` || '',
      'Content-Type': 'application/json',
    },
  });

  const fetchMovements = async () => {
    try {
      const res = await axios.get(`${API}/stock-movements`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          sort,
          order,
        },
      });
      setMovements(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const handleExport = () => {
   const exportData = movements.map((item) => ({
	  Date: dayjs(item.createdAt).format('DD/MM/YYYY HH:mm'),
	  Product: item.productName || '-',
	  Location: item.locationName || '-',
	  Type: item.movementType || '-',
	  Qty: item.qty,
	}));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'StockMovements');
    XLSX.writeFile(workbook, 'StockMovementsList.xlsx');
  };

  const handleSort = (field: string) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('asc');
    }
  };

  useEffect(() => {
    fetchMovements();
  }, [currentPage, rowsPerPage, sort, order]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchMovements();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const maxPage = Math.ceil(total / rowsPerPage);

  const sortIcon = (field: string) => {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Stock Movement List</h2>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-[13px] w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-[13px]"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>{num} / page</option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto border rounded mb-2">
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-100 text-gray-700">
			  <tr>
				<th
				  className="border px-2 py-2 cursor-pointer select-none"
				  onClick={() => handleSort("createdAt")}
				>
				  Date {sort === "createdAt" && (order === "asc" ? "▲" : "▼")}
				</th>
				<th
				  className="border px-2 py-1 cursor-pointer select-none"
				  onClick={() => handleSort("productName")}
				>
				  Product {sort === "productName" && (order === "asc" ? "▲" : "▼")}
				</th>
				<th
				  className="border px-2 py-1 cursor-pointer select-none"
				  onClick={() => handleSort("locationName")}
				>
				  Location {sort === "locationName" && (order === "asc" ? "▲" : "▼")}
				</th>
				<th
				  className="border px-2 py-1 cursor-pointer select-none"
				  onClick={() => handleSort("movementType")}
				>
				  Type {sort === "movementType" && (order === "asc" ? "▲" : "▼")}
				</th>
				<th
				  className="border px-2 py-1 cursor-pointer select-none"
				  onClick={() => handleSort("qty")}
				>
				  Qty {sort === "qty" && (order === "asc" ? "▲" : "▼")}
				</th>
			  </tr>
			</thead>

            <tbody>
              {movements.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1">{dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}</td>
                  <td className="border px-2 py-1">{item.productName || '-'}</td>
                  <td className="border px-2 py-1">{item.locationName || '-'}</td>
                  <td className="border px-2 py-1">{item.movementType}</td>
                  <td className="border px-2 py-1 text-right">{item.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 border rounded">
              <ChevronsLeft size={18} />
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-2 py-1 border rounded">«</button>
            {Array.from({ length: Math.ceil(total / rowsPerPage) }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${page === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, maxPage))} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">»</button>
            <button onClick={() => setCurrentPage(maxPage)} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMovementsList;
